import { Box, HStack, Image, Text } from '@chakra-ui/react';

import { RewardCategory } from '@/types';

import { SpinTickerItem } from './FortuneWheel';

import { GoonAvatar } from '@/components/goon-avatar';
import { RoundedBadge } from '@/components/rounded-badge';
import { useEffect, useState } from 'react';
import { REWARD_CATEGORY_METADATA } from './constants';

export const FloatingRewardsBackground = ({ recentSpins }: { recentSpins: SpinTickerItem[] }) => {
  // Track position data for each floating reward
  const [positions, setPositions] = useState<{ [key: string]: React.CSSProperties }>({});

  // Create new position when a reward is added
  useEffect(() => {
    const newPositions = { ...positions };
    const timeouts: NodeJS.Timeout[] = [];

    recentSpins.forEach((spin) => {
      // If position exists already, skip
      if (newPositions[spin.id]) return;

      // Create initial position with no opacity transition
      newPositions[spin.id] = {
        left: `${Math.random() * 100}%`, // Random horizontal position
        bottom: '-50px', // Start below the container
        transform: 'translateX(-50%)', // Center horizontally
        transition: `bottom ${10 + Math.random() * 15}s linear`, // Random speed, no opacity fade
        opacity: 0.7, // Fixed opacity
      };

      // Schedule position update to animate upward (only change position, not opacity)
      timeouts.push(
        setTimeout(() => {
          setPositions((prev) => ({
            ...prev,
            [spin.id]: {
              ...prev[spin.id],
              bottom: '120%', // Move past the top
              // No opacity change
            },
          }));
        }, 100),
      );

      // Schedule removal after animation completes
      // Give it more time to ensure it's completely off-screen before removing
      timeouts.push(
        setTimeout(() => {
          setPositions((prev) => {
            const updated = { ...prev };
            delete updated[spin.id];
            return updated;
          });
        }, 30000),
      ); // 30 seconds instead of 25
    });

    if (Object.keys(newPositions).length > 0) {
      setPositions(newPositions);
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [recentSpins]);

  return (
    <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={1} overflow="hidden" pointerEvents="none">
      {recentSpins.map((spin) =>
        positions[spin.id] ? <FloatingReward key={spin.id} item={spin} style={positions[spin.id]} /> : null,
      )}
    </Box>
  );
};

const FloatingReward = ({ item, style }: { item: SpinTickerItem; style: React.CSSProperties }) => {
  const shortenAddress = (address: string) => {
    return `${address.substring(0, 3)}...${address.substring(address.length - 3)}`;
  };
  const { icon, rewardShorthand } = REWARD_CATEGORY_METADATA[item.rewardCategory as RewardCategory];

  return (
    <HStack
      gap={1}
      align="center"
      color="#a9a9a9"
      fontSize="sm"
      bg="rgba(255, 255, 255, 0.85)"
      borderRadius="full"
      px={2}
      py={1}
      boxShadow="0 2px 6px rgba(0,0,0,0.1)"
      alignItems="center"
      position="absolute"
      style={style}
    >
      <GoonAvatar seed={item.walletAddress.toLowerCase()} boxSize="22px" />
      <Text fontSize="14px" mb={-0.5} color="#27272A">
        {shortenAddress(item.walletAddress)}
      </Text>
      <RoundedBadge py={1} px={2} ml={2} overflow="hidden">
        <Image src={icon} boxSize="18px" />
        <Text color="fg.subtle">{item.rewardAmount}</Text>
        <Text color="fg.subtle">{rewardShorthand}</Text>
      </RoundedBadge>
    </HStack>
  );
};
